(ns admin.events.moments
  (:require [applied-science.js-interop :as j]
            [re-frame.core :as re-frame]

            [admin.utils :as au]

            [kyber.lib.kyber-db :as kyber-db]
            [kyber.lib.kyber-eql :refer [defmutate]]
            [kyber.events.eql :as eql]
            [kyber.utils :as ku]
            [kyber.events.utils :refer [add-auth base-uri kyber-event-fx kyber-event-db]]
            [kyber.kyber-db.activity-resource :as activity-resource]))

(def overlapping-moment-message
  [:notifier/create-alert
   {:title "Something doesn't look right..."
    :message (str "Seems like your learning moment's display time "
                  "is overlapping another moment. This would cause "
                  "multiple moments to be displayed on the screen "
                  "at the same time. Adjust your display time "
                  "to create your moment.")
    :confirm-only true}])

;; Default number of seconds that are added to the front end of a moment
(defonce start-time-lead-time 10)
;; Default number of seconds that will be added to display-at for setting
;; a whispers hide-at
(defonce whisper-hide-at-trail-time 10)
;; Default number of seconds that will be added to the display-at time
;; for all moment types
(defonce end-time-trail-time 15)

(defn- moment-is-valid?
  "Returns true if the moment passes validation"
  [{:keys [moments moment-uuid]}]
  (let [sorted-moments
        (->> moments
             (vals)
             (remove #(or (= moment-uuid (:uuid %))
                          (= moment-uuid (:temp-uuid %))))
             (sort-by :display-at))

        {:keys [display-at hide-at] :as target-moment}
        (get moments moment-uuid)]

    (cond
      (and (ku/video-moment? target-moment)
           (some? display-at)
           (some? hide-at))
      (->> sorted-moments
           (filter #(or
                     ;; Moments can not display in the middle of whispers
                     ;; or at the same time as another moment
                     (and (>= display-at (:display-at %))
                          (<= display-at (:hide-at %)))
                     (and (>= hide-at (:display-at %))
                          (<= hide-at (:hide-at %)))
                     ;; Whispers cannot span over other moments
                     (and (<= display-at (:display-at %))
                          (>= hide-at (:hide-at %)))))
           (empty?))

      :else
      true)))

(defn- momentv1->momentv2
  [plo-type {:keys [type behavior] :as moment}]
  (if (nil? behavior)
    (merge moment
           (condp = type
             "educator-note"
             {:type "note"
              :behavior
              (condp = plo-type
                "video" "shout"
                "pdf" "whisper"
                "none")}

             "whisper"
             {:type "note"
              :behavior "whisper"}

             "question"
             {:behavior
              (condp = plo-type
                "video" "shout"
                "pdf" "whisper"
                "none")}
             moment))
    moment))


;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; adjust-moment-halo
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-adjust-moment-halo
    {:description "Adjusts a moment's halo"
     :changelog
     [{:date "11FEB19"
       :event-version 0
       :data {}}]}))

(defn adjust-moment-halo
  [{:keys [db]}
   [event-name
    {:keys [moment-uuid halo-property time moment-behavior player-key]}]]
  (let [{:keys [display-at hide-at start-time end-time]}
        (get-in db [:moments moment-uuid])]
    {:db (cond-> (-> db
                     (assoc-in [:moments moment-uuid halo-property]
                               time)
                     (assoc-in [:moments moment-uuid :edit-state :is-invalid]
                               (not (moment-is-valid?
                                     {:moments (:moments db)
                                      :moment-uuid moment-uuid}))))
           ;; Ensures display-at is always before hide-at
           (and (= halo-property :hide-at) (< time display-at))
           (assoc-in [:moments moment-uuid :display-at] time)

           ;; Ensures hide-at is always after display-at
           (and (= halo-property :display-at) (> time hide-at))
           (assoc-in [:moments moment-uuid :hide-at] time)

           ;; Ensures start time is always the first marker
           (< time start-time)
           (assoc-in [:moments moment-uuid :start-time] time)

           (and (= halo-property :start-time) (> time display-at))
           (assoc-in [:moments moment-uuid :display-at] time)

           (and (= halo-property :start-time) (> time hide-at))
           (assoc-in [:moments moment-uuid :hide-at] time)

           ;; Ensures end time is always the last marker
           (> time end-time)
           (assoc-in [:moments moment-uuid :end-time] time)

           (and (= halo-property :end-time) (< time hide-at))
           (assoc-in [:moments moment-uuid :hide-at] time)

           (and (= halo-property :end-time) (< time display-at))
           (assoc-in [:moments moment-uuid :display-at] time)

           ;; Keeps display-at and hide-at in sync for non whisper moments
           (and (= halo-property :display-at) (= moment-behavior "shout"))
           (assoc-in [:moments moment-uuid :hide-at] time))
     :dispatch-n (cond-> [[:analytics/save-datapoint {:event event-name
                                                      :event-version 0}]]
                   (some? player-key)
                   (conj [:media-player/seek {:player-key player-key
                                              :position time}]))}))

(kyber-event-fx :moments/adjust-moment-halo adjust-moment-halo)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; get-moments
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-moments-success
    {:description "Get moments success"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-moments-success
  [{:keys [db]} [event-name response]]
  {:db (assoc db :moments (into {} (map (juxt :uuid identity) response)))
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :moments/get-moments-success
                get-moments-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-moments-failure
    {:description "Get moments failure"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-moments-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :moments/get-moments-failure
                get-moments-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-moments
    {:description "Get moments request"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-moments
  [{:keys [db]} [event-name {:keys [activity-uuid]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   {:name event-name
    :method :get
    :uri (base-uri db (str "/v2/admin/activities/" activity-uuid "/moments"))
    :response-format :json
    :headers (add-auth db)
    :on-success [:moments/get-moments-success]
    :on-failure [:moments/get-moments-failure]}})

(kyber-event-fx :moments/get-moments get-moments)


;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; update-moment
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-update-moment-success
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn legacy-update-moment-success
  [{:keys [db]} [event-name response]]
  (let [{:keys [activity-uuid curriculum-uuid]} (get-in db [:router :route-params])
        {:keys [plo-type]} (get-in db [:curricula curriculum-uuid
                                       :activities activity-uuid])
        xformed-moment (momentv1->momentv2 plo-type response)]
    {:db (update-in db [:moments (:uuid xformed-moment)] merge xformed-moment)
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]
                  [:notifier/create-toast {:message "Moment updated."}]]}))

(kyber-event-fx :moments/update-moment-success
                legacy-update-moment-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-update-moment-failure
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn legacy-update-moment-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :moments/update-moment-failure
                legacy-update-moment-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-update-moment
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn legacy-update-moment
  [{:keys [db]} [event-name moment]]
  (let [existing-moment (get-in db [:moments (:uuid moment)])
        updated-moment (merge existing-moment
                              (dissoc moment :should-transition))
        is-valid-moment (moment-is-valid? {:moment-uuid (:uuid updated-moment)
                                           :moments (:moments db)})
        ;; Note: The legacy moment model expects :activity-uuid to be on the
        ;;       moment model. Moving forward it seems it will be more
        ;;       consistent to have :activity of type UUID or the
        ;;       activity model (Depending on how it's requested from the
        ;;       api). TODO: Ideally all refrence objects would be
        ;;       {:activity {:uuid ""}} or the full model.
        activity-uuid (or (:activity-uuid updated-moment)
                          (:activity updated-moment))]
    (cond->
     {:db db
      :dispatch-n [[:analytics/save-datapoint {:event event-name
                                               :event-version 0}]]}
      (true? is-valid-moment)
      (assoc
       :request
       {:name event-name
        :method :patch
        :uri (base-uri db (str "/v2/admin/activities/" activity-uuid
                               "/moments/" (:uuid moment)))
        :params (dissoc updated-moment
                        :uuid
                        :edit-state
                        :activity
                        :activity-uuid
                        ;; Note: This properties are all managed through EQL
                        :aggregate-results
                        :admin-message
                        :valid
                        :type
                        :behavior
                        :start-time
                        :end-time
                        :hide-at
                        :show-at
                        :xfdf
                        :xfdf-id)
        :format :json
        :response-format :json
        :headers (add-auth db)
        :on-success [:moments/update-moment-success]
        :on-failure [:moments/update-moment-failure]})

      (false? is-valid-moment)
      (update :dispatch-n conj overlapping-moment-message))))

(kyber-event-fx :moments/update-moment
                legacy-update-moment)


;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; moment-remove-resource
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-moment-remove-resource
    {:description "Removes a resource from a moment"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn moment-remove-resource
  [{:keys [db]} [event-name {:keys [moment-uuid
                                    resource-uuid
                                    curriculum-uuid
                                    activity-uuid
                                    highlighted-resource]}]]
  {:db db
   :dispatch-n
   [[:notifier/create-alert
     {:title "Remove resource from the moment?"
      :message (str "Are you sure you want to remove this "
                    "resource from the moment?")
      :confirm-action
      (fn []
        (when (= highlighted-resource resource-uuid)
          (re-frame/dispatch
           [::update-moment
            {:uuid moment-uuid
             :curriculum {:curriculum/uuid curriculum-uuid}
             :activity {:activity/uuid activity-uuid}
             :highlighted-resource nil}
            {}
            [:uuid :activity :highlighted-resource]]))
        (re-frame/dispatch
         [:moments/update-moment
          {:uuid moment-uuid
           :resources (filterv
                       #(not= resource-uuid (:uuid %))
                       (get-in db [:moments moment-uuid :resources]))
           :should-transition false}]))
      :confirm-text "Remove resource"
      :deny-text "Keep Resource"}]
    [:analytics/save-datapoint {:event event-name
                                :event-version 0}]]})

(kyber-event-fx :moments/moment-remove-resource moment-remove-resource)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; moment-add-resource
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-moment-add-resource
    {:description "Adds a resource from a moment"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn moment-add-resource
  [{:keys [db]} [event-name {:keys [moment-uuid
                                    resource-uuid
                                    activity-uuid
                                    curriculum-uuid
                                    learning-objective]
                             :as props}]]
  (let [moment-resources (get-in db [:moments moment-uuid :resources])
        activity-resource (get-in db [:activity-resources resource-uuid])
        exists-in-moment (->> moment-resources
                              (filter #(= resource-uuid (:uuid %)))
                              (first)
                              (some?))]
    {:db db
     :dispatch-n
     (cond-> [[:analytics/save-datapoint {:event event-name
                                          :event-version 0}]]
       (and (some? activity-resource)
            (false? exists-in-moment))
       (conj [:moments/update-moment
              {:uuid moment-uuid
               :resources (conj moment-resources activity-resource)
               :should-transition false}])

       (nil? activity-resource)
       (conj [:activity-resources/create-activity-resource
              (cond-> {:activity-uuid activity-uuid
                       :curriculum-uuid curriculum-uuid
                       :resource resource-uuid
                       :on-success #(re-frame/dispatch
                                     [:moments/moment-add-resource
                                      (select-keys props [:moment-uuid
                                                          :resource-uuid])])}
                (some? learning-objective)
                (assoc :learning-objective learning-objective))]))}))

(kyber-event-fx :moments/moment-add-resource moment-add-resource)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; add-pdf-moment
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-add-pdf-moment
    {:description "Initializes the PDF moment creation workflow"
     :changelog
     [{:date "03APR20"
       :event-version 0
       :data {}}]}))

(defn add-pdf-moment
  [{:keys [db]} [event-name
                 {:keys [activity-uuid curriculum-uuid]}
                 xfdf
                 xfdf-id]]
  (let [temp-moment-uuid
        (str (random-uuid))

        temp-moment
        {:temp-uuid temp-moment-uuid
         :xfdf xfdf
         :xfdf-id xfdf-id
         :edit-state {:step :moment-type-selection
                      :activity-uuid activity-uuid
                      :curriculum-uuid curriculum-uuid}}]
    {:db (assoc-in db [:moments temp-moment-uuid] temp-moment)
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]}))

(kyber-event-fx :moments/add-pdf-moment add-pdf-moment)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; add-moment-type-to-temp-moment
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-add-moment-type-to-temp-moment
    {:description
     (str "Adds the moment type to a temporary moment progressing "
          "it to the next step in the moment creation workflow.")
     :changelog
     [{:date "03APR20"
       :event-version 0
       :data {}}]}))

(defn add-moment-type-to-temp-moment
  [{:keys [db]} [event-name {:keys [moment moment-type moment-behavior]}]]
  (let [temp-uuid (:temp-uuid moment)]
    {:db (-> db
             (assoc-in [:moments temp-uuid :type] (name moment-type))
             (assoc-in [:moments temp-uuid :behavior] (name moment-behavior))
             (assoc-in [:moments temp-uuid :edit-state :step]
                       (if (= moment-type :question)
                         :question
                         :moment-details)))
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]}))

(kyber-event-fx :moments/add-moment-type-to-temp-moment
                add-moment-type-to-temp-moment)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; update-pdf-moment-xfdf
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-update-pdf-moment-xfdf
    {:description "Updates the XFDF associated with a moment"
     :changelog
     [{:date "03APR20"
       :event-version 0
       :data {}}]}))

(defn update-pdf-moment-xfdf
  [{:keys [db]} [event-name _ xfdf xfdf-id]]
  (let [moment
        (->> db
             :moments
             vals
             (filter #(= (:xfdf-id %) xfdf-id))
             first)]
    {:db db
     :dispatch-n [[:moments/update-moment (assoc moment :xfdf xfdf)]
                  [:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]}))

(kyber-event-fx :moments/update-pdf-moment-xfdf
                update-pdf-moment-xfdf)

;; Legacy ^
;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; New ⌄


;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; moment-annotation-imported
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn fx-moment-annotation-imported [annotations]
  (some-> annotations
          (first)
          (j/assoc! :ReadOnly true)))

(re-frame/reg-fx ::moment-annotation-imported fx-moment-annotation-imported)

(when ^boolean js/goog.DEBUG
  (def event-doc-moment-annotation-imported
    {:description "An moment annotation was imported into the PDF"
     :changelog
     [{:date "11NOV20"
       :event-version 0
       :data {}}]}))

(defn moment-annotation-imported
  [{:keys [db]} [_event-name moment]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event :moments/moment-annotation-imported
                                            :event-version 0}]]
   ::moment-annotation-imported moment})

(kyber-event-fx ::moment-annotation-imported moment-annotation-imported)


;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Note: Below are the prefered moment events. The above will be removed in
;;       the future when the legacy state management pattern has been removed.
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defmutate delete-moment
  {:model-key :moment
   :mutation-symbol 'moment/delete
   :event-namespace ::self
   :analytics-key :moments/delete-moment
   :update-fn (fn [db _request response]
                (-> db
                    (kyber-db/retract-record {:model :moment} response)
                    (kyber-db/retract-ref {:model :activity
                                           :refs-key :activity/moments
                                           :ref-ident-key (:moment/uuid response)}
                                          {:activity/uuid (:activity/uuid response)})))})


(def base-moment-keys
  [:moment/uuid
   :moment/activity
   :moment/admin-message
   :moment/behavior
   :moment/body
   :moment/confidence
   :moment/highlighted-resource
   {:moment/learning-objective
    [:learning-objective/uuid
     :learning-objective/description
     :learning-objective/label]}
   :moment/include-poll-data
   {:moment/valid-tags
    [:content-provider.tag-group/uuid
     :content-provider.tag-group/name
     :content-provider.tag-group/description
     {:content-provider.tag-group/values
      [:content-provider.tag/uuid
       :content-provider.tag/name]}]}
   {:moment/question
    [:question/body
     {:question/choices [:question.choice/body
                         :question.choice/correct-answer
                         :question.choice/uuid]}
     :question/educator-feedback
     :question/has-correctness
     :question/type
     :question/key?
     :question/uuid]}
   :moment/required
   :moment/resources
   :moment/title
   :moment/type
   :moment/valid])

(def pdf-moment-keys
  [:moment/xfdf
   :moment/xfdf-id
   :moment/page-number
   :moment/halo-rects
   :moment/x1
   :moment/x2
   :moment/y1
   :moment/y2])

(def video-moment-keys
  [:moment/display-at
   :moment/end-time
   :moment/hide-at
   :moment/start-time])

(defn get-response-keys [plo-type]
  (apply conj base-moment-keys
         (case plo-type
           "video" video-moment-keys
           "pdf" pdf-moment-keys
           (js/console.error "PLO-type is not supported: " plo-type))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; update-moment
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn update-moment-success
  [{:keys [db]} [_ {activity :moment/activity} {:keys [on-success]} {moment 'moment/update}]]
  (cond-> {:db (kyber-db/tx-record db
                                   {:model :activity}
                                   (assoc activity :activity/moments [moment]))
           :dispatch-n [[:analytics/save-datapoint {:event :moments/update-moment-success
                                                    :event-version 0}]]}
    (fn? on-success)
    (assoc :invoke on-success)))

(kyber-event-fx ::update-moment-success update-moment-success)

(defn update-moment-failure
  [{:keys [db]} [_ _ {:keys [on-failure]} _err]]
  (cond-> {:db db
           :dispatch-n [[:analytics/save-datapoint {:event :moments/update-moment-failure
                                                    :event-version 0}]]}
    (fn? on-failure)
    (assoc :invoke on-failure)))

(kyber-event-fx ::update-moment-failure update-moment-failure)

(defn update-moment
  [{:keys [db]} [_ params options]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event :activities/update-moment
                                            :event-version 0}]]
   :request
   (eql/request
    db
    {:name ::update-moment
     :params `[{(moment/update ~params) ~(get-response-keys (:plo-type options))}]
     :on-success [::update-moment-success params options]
     :on-failure [::update-moment-failure params options]})})

(kyber-event-fx ::update-moment update-moment)

(defn update-moment-annotation
  [{:keys [db]} [_ {:keys [viewer-key activity-uuid curriculum-uuid]} xfdf xfdf-id]]
  (let [active-moment-uuid (:active-moment-uuid db)]
    {:dispatch-n [[:pdftron-viewer/on-tool-select
                   {:options {:viewer-key viewer-key}
                    :tool-name "AnnotationEdit"}]
                  [::update-moment
                   {:moment/uuid active-moment-uuid
                    :moment/activity {:activity/uuid activity-uuid}
                    :moment/curriculum {:curriculum/uuid curriculum-uuid}
                    :moment/xfdf xfdf
                    :moment/xfdf-id xfdf-id}
                   {:plo-type "pdf"
                    :on-success-toast "Moment placement updated"}]
                  [:dispatch-in 1400
                   ::get-moments-v2
                   {:activity-uuid activity-uuid}
                   {:plo-type "pdf"}]]}))

(kyber-event-fx ::update-moment-annotation update-moment-annotation)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; create-moment
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn set-active-moment-uuid
  [db [_ moment-uuid]]
  (assoc db :active-moment-uuid moment-uuid))

(kyber-event-db ::set-active-moment-uuid set-active-moment-uuid)

(defn unset-active-moment-uuid
  [db _]
  (dissoc db :active-moment-uuid))

(kyber-event-db ::unset-active-moment-uuid unset-active-moment-uuid)

(defmutate create-moment
  {:model-key :moment
   :mutation-symbol 'moment/create
   :event-namespace ::self
   :analytics-key :moments/create-moment
   :update-fn
   (fn [db _request moment]
     (let [activity
           (kyber-db/get-record db :activity (get-in moment [:moment/activity :activity/uuid]))]
       (if (some? activity)
         (kyber-db/tx-record db
                             {:model :activity}
                             (assoc activity
                                    :activity/moments
                                    [(dissoc moment :moment/activity)]))
         (js/console.error "Missing activity in moment/create response"))))})

(defmutate add-resource
  {:model-key :moment
   :mutation-symbol 'moment.resource/add
   :event-namespace ::self
   :analytics-key :moment.resource/add
   :update-fn
   (fn [db _request response]
     (kyber-db/tx-record
      db
      {:model :moment}
      {:moment/uuid (:moment/uuid response)
       :moment/resources [(dissoc response :moment/uuid)]}))})

(defmutate remove-resource
  {:model-key :moment
   :mutation-symbol 'moment.resource/remove
   :event-namespace ::self
   :analytics-key :moment.resource/remove
   :update-fn
   (fn [db request _response-body]
     (kyber-db/retract-ref
      db
      {:model :moment
       :refs-key :moment/resources
       :ref-ident-key (activity-resource/->ident-key
                       {:activity/uuid (:activity/uuid request)
                        :resource/uuid (:resource/uuid request)})}
      {:moment/uuid (:moment/uuid request)}))})

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; add-video-moment
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-add-video-moment
    {:description "Add a moment in a video PLO"
     :changelog
     [{:date "16DEC20"
       :event-version 0
       :data {}}]}))

(defn add-video-moment
  [{:keys [db]} [_event-name
                 {:keys [time
                         duration
                         type
                         behavior
                         activity-uuid
                         curriculum-uuid]}
                 on-valid-location-callback]]
  (let [display-at (.round js/Math time)
        hide-at (if (= behavior "whisper")
                  (.round js/Math
                          (min duration
                               (+ time whisper-hide-at-trail-time)))
                  (.round js/Math time))
        ;; Halo start
        start-time (.round js/Math
                           (max 0 (- time start-time-lead-time)))
        ;; Halo end
        end-time (.round js/Math
                         (min duration
                              (+ time end-time-trail-time)))
        valid-video-moment-location
        (au/valid-video-moment-location?
         {:moments (kyber-db/get-records db :moment (kyber-db/get-sort db :moment))
          :display-at display-at
          :hide-at hide-at})]
    (cond-> {:db db
             :dispatch-n [[:analytics/save-datapoint {:event :moments/add-video-moment
                                                      :event-version 0}]]}
      (true? valid-video-moment-location)
      (update :dispatch-n conj
              [::create-moment
               {:moment/activity {:activity/uuid activity-uuid}
                :moment/curriculum {:curriculum/uuid curriculum-uuid}
                :moment/type type
                :moment/behavior behavior
                :moment/start-time start-time
                :moment/end-time end-time
                :moment/display-at display-at
                :moment/hide-at hide-at}
               {:response-keys (get-response-keys "video")
                :on-success-toast "Moment added"
                :on-success #(re-frame/dispatch [::set-active-moment-uuid (:moment/uuid %)])}])

      (false? valid-video-moment-location)
      (update :dispatch-n conj overlapping-moment-message)

      (and (fn? on-valid-location-callback)
           (true? valid-video-moment-location))
      (assoc :invoke on-valid-location-callback))))

(kyber-event-fx ::add-video-moment add-video-moment)


;; ;;;;;;;;;;;;;;;;;;;;;;;;;
;; get all moments
;; ;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-moments-v2-success
    {:description "Get activity moments (Success)"
     :changelog
     [{:date "15APR21"
       :event-version 0
       :data {}}]}))

(defn get-moments-v2-success
  [{:keys [db]} [event-name {:keys [activity-uuid]} res]]
  (let [activity (get res [:activity/uuid activity-uuid])]
    {:db (kyber-db/tx-record db  {:model :activity
                                  :resort-child-idents true} activity)
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]}))

(kyber-event-fx ::get-moments-v2-success get-moments-v2-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-moments-v2-failure
    {:description "Get activity moments (Failure)"
     :changelog
     [{:date "15APR21"
       :event-version 0
       :data {}}]}))

(defn get-moments-v2-failure
  [{:keys [db]} [event-name _ _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::get-moments-v2-failure get-moments-v2-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-moments-v2
    {:description "Get activity moments"
     :changelog
     [{:date "15APR21"
       :event-version 0
       :data {}}]}))

(defn get-moments-v2
  [{:keys [db]} [event-name {:keys [activity-uuid] :as params} options]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   (eql/request
    db
    {:name event-name
     :params `[{[:activity/uuid ~activity-uuid]
                [:activity/uuid
                 {(:activity/moments {:include-invalid? true}) ~(get-response-keys (:plo-type options))}]}]
     :on-success [::get-moments-v2-success params]
     :on-failure [::get-moments-v2-failure params]})})

(kyber-event-fx ::get-moments-v2 get-moments-v2)
